(() => {
  const sectionSelector = '[data-event-carousel]';
  const eventSelector = '[data-event-carousel-event]';
  const trackSelector = '[data-event-carousel-track]';

  const sectionsIn = (scope = document) => {
    const sections = Array.from(scope.querySelectorAll?.(sectionSelector) || []);

    if (scope.matches?.(sectionSelector)) sections.unshift(scope);

    return sections;
  };

  const parseIsoDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;

    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    return Number.isNaN(date.getTime()) ? null : date;
  };

  class EventCarousel {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector(trackSelector);
      this.loading = section.querySelector('[data-event-carousel-loading]');
      this.empty = section.querySelector('[data-event-carousel-empty]');
      this.fetchUrl = section.dataset.eventCarouselFetchUrl || '';
      this.today = section.dataset.eventCarouselToday || '';
      this.limit = Number.parseInt(section.dataset.eventCarouselLimit || '9', 10);
      this.locale = section.dataset.eventCarouselLocale || undefined;
      this.designMode = section.dataset.eventCarouselDesignMode === 'true';

      if (!this.track) return;

      this.monthFormatter = new Intl.DateTimeFormat(this.locale, {
        month: 'short',
        timeZone: 'UTC'
      });
      this.dayFormatter = new Intl.DateTimeFormat(this.locale, {
        day: 'numeric',
        timeZone: 'UTC'
      });

      this.loadEvents();
    }

    async loadEvents() {
      try {
        if (!this.fetchUrl) throw new Error('Event carousel URL is missing');

        const response = await fetch(this.fetchUrl, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Event carousel request failed: ${response.status}`);

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const results = parsed.querySelector('[data-event-calendar-results]');
        const cards = Array.from(results?.querySelectorAll(eventSelector) || [])
          .filter((card) => card.dataset.eventDate >= this.today)
          .sort((a, b) => {
            const dateOrder = a.dataset.eventDate.localeCompare(b.dataset.eventDate);
            if (dateOrder !== 0) return dateOrder;

            return (a.dataset.eventTitle || '').localeCompare(b.dataset.eventTitle || '', this.locale);
          })
          .slice(0, Number.isNaN(this.limit) ? 9 : this.limit);

        if (cards.length > 0) {
          this.track.replaceChildren(...cards);
          cards.forEach((card) => this.localizeDate(card));
          this.empty && (this.empty.hidden = true);
        } else if (!this.designMode) {
          this.track.replaceChildren();
          this.empty && (this.empty.hidden = false);
        }
      } catch (error) {
        if (!this.designMode) {
          this.track.replaceChildren();
          this.empty && (this.empty.hidden = false);
        }
      } finally {
        this.loading && (this.loading.hidden = true);
        this.section.dispatchEvent(new CustomEvent('carousel:content-updated', { bubbles: true }));
      }
    }

    localizeDate(card) {
      const date = parseIsoDate(card.dataset.eventDate);
      if (!date) return;

      const month = card.querySelector('[data-event-carousel-month]');
      const day = card.querySelector('[data-event-carousel-day]');

      if (month) month.textContent = this.monthFormatter.format(date);
      if (day) day.textContent = this.dayFormatter.format(date);
    }
  }

  const initialize = (scope = document) => {
    sectionsIn(scope).forEach((section) => {
      if (section.hasAttribute('data-event-carousel-initialized')) return;

      section.setAttribute('data-event-carousel-initialized', 'true');
      new EventCarousel(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
